import { appendQualitySlide } from './reportQualityExport';
// Generate PowerPoint report for Data Maturity Assessment
import pptxgen from 'pptxgenjs';

export const generateDataMaturityPptx = (results, fullResults) => {
  const pptx = new pptxgen();
  
  // Set presentation properties
  pptx.author = 'SAMANVAI';
  pptx.title = `Data Maturity Assessment - ${results.company_name}`;
  pptx.subject = 'Data Management Maturity Report';
  pptx.company = 'SAMANVAI';
  
  // Color scheme - Professional Data theme
  const colors = {
    primary: '6366F1',    // Indigo
    secondary: '8B5CF6',  // Purple
    accent: '0EA5E9',     // Sky blue
    dark: '1E1B4B',       // Indigo 950
    darkGray: '1F2937',   // Gray 800
    mediumGray: '4B5563', // Gray 600
    lightGray: 'F3F4F6',  // Gray 100
    white: 'FFFFFF',
    red: 'EF4444',
    orange: 'F97316',
    amber: 'F59E0B',
    green: '22C55E',
    emerald: '10B981'
  };
  
  const getScoreColor = (score) => {
    if (score < 1.5) return colors.red;
    if (score < 2.5) return colors.orange;
    if (score < 3.5) return colors.amber;
    if (score < 4.5) return colors.emerald;
    return colors.green;
  };

  const getStatusText = (score, benchmark) => {
    const gap = score - benchmark;
    if (gap < -0.8) return 'Critical Gap';
    if (gap < -0.3) return 'Below Average';
    if (gap < 0.3) return 'At Par';
    return 'Above Average';
  };

  const maturityLevels = [
    { level: 1, name: 'Initial', desc: 'Ad-hoc data management, no formal processes' },
    { level: 2, name: 'Developing', desc: 'Basic processes emerging, reactive approach' },
    { level: 3, name: 'Defined', desc: 'Standardized processes, proactive management' },
    { level: 4, name: 'Managed', desc: 'Quantified metrics, continuous improvement' },
    { level: 5, name: 'Optimizing', desc: 'Industry-leading, data-driven culture' }
  ];

  const data = fullResults || results;
  const benchmark = data.industry_benchmark || { avg: 2.5, top: 4.0 };
  const overallScore = data.overall_score || results.overall_score || 2.0;
  const dimensions = data.dimension_scores || results.dimension_scores || [];
  const maturityLevel = data.overall_maturity_level || data.maturity_level?.name || 'Developing';

  // ============== Slide 1: Title Slide ==============
  const slide1 = pptx.addSlide();
  slide1.background = { color: colors.dark };
  
  // Logo/Brand
  slide1.addText('SAMANVAI', {
    x: 0.5, y: 0.4, w: 3, h: 0.5,
    fontSize: 24, bold: true, color: colors.primary
  });
  
  slide1.addText('Agentic AI for CXOs', {
    x: 0.5, y: 0.8, w: 3, h: 0.3,
    fontSize: 10, color: colors.accent
  });
  
  // Main title
  slide1.addText('Data Management', {
    x: 0.5, y: 2, w: 7, h: 0.9,
    fontSize: 44, bold: true, color: colors.white
  });
  
  slide1.addText('Maturity Assessment', {
    x: 0.5, y: 2.8, w: 7, h: 0.9,
    fontSize: 44, bold: true, color: colors.white
  });
  
  // Subtitle
  slide1.addText('C-suite Ready Insight Report', {
    x: 0.5, y: 3.7, w: 5, h: 0.5,
    fontSize: 20, color: colors.secondary
  });
  
  // Company name
  slide1.addText(data.company_name || results.company_name, {
    x: 0.5, y: 4.4, w: 6, h: 0.6,
    fontSize: 28, color: colors.accent
  });
  
  // Meta info
  slide1.addText(`${data.industry || results.industry} | ${data.company_size || 'Enterprise'}`, {
    x: 0.5, y: 5.0, w: 6, h: 0.4,
    fontSize: 12, color: 'A0AEC0'
  });
  
  // Date
  slide1.addText(`Assessment Date: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, {
    x: 0.5, y: 5.4, w: 5, h: 0.3,
    fontSize: 11, color: '718096'
  });
  
  // Overall Score Badge
  slide1.addShape('rect', {
    x: 7.3, y: 1.6, w: 2.2, h: 3,
    fill: { color: getScoreColor(overallScore) },
    line: { color: colors.white, width: 2 }
  });
  slide1.addText(overallScore.toFixed(1), {
    x: 7.3, y: 1.9, w: 2.2, h: 1.2,
    fontSize: 52, bold: true, color: colors.white, align: 'center'
  });
  slide1.addText('out of 5.0', {
    x: 7.3, y: 3.0, w: 2.2, h: 0.3,
    fontSize: 11, color: colors.white, align: 'center'
  });
  slide1.addText(data.maturity_level?.level || data.overall_maturity_level || 'Developing', {
    x: 7.3, y: 3.4, w: 2.2, h: 0.5,
    fontSize: 10, bold: true, color: colors.white, align: 'center', wrap: true
  });
  slide1.addText(getStatusText(overallScore, benchmark.avg), {
    x: 7.3, y: 4.0, w: 2.2, h: 0.3,
    fontSize: 9, color: colors.white, align: 'center'
  });

  // ============== Slide 2: Executive Summary ==============
  const slide2 = pptx.addSlide();
  
  // Header
  slide2.addShape('rect', {
    x: 0, y: 0, w: 10, h: 1,
    fill: { color: colors.primary }
  });
  slide2.addText('Executive Summary', {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 28, bold: true, color: colors.white
  });
  
  // Key Stats Row
  const stats = [
    { label: 'Your Score', value: overallScore.toFixed(1), color: getScoreColor(overallScore) },
    { label: 'Industry Average', value: String(benchmark.avg || 2.5), color: colors.mediumGray },
    { label: 'Top Performers', value: String(benchmark.top || 4.0), color: colors.emerald },
    { label: 'Gap to Average', value: (overallScore - (benchmark.avg || 2.5)).toFixed(2), color: overallScore >= (benchmark.avg || 2.5) ? colors.green : colors.red }
  ];
  
  stats.forEach((stat, idx) => {
    const x = 0.5 + idx * 2.4;
    slide2.addShape('rect', {
      x: x, y: 1.3, w: 2.2, h: 1.1,
      fill: { color: colors.lightGray }
    });
    slide2.addText(stat.label, {
      x: x, y: 1.4, w: 2.2, h: 0.3,
      fontSize: 9, color: colors.mediumGray, align: 'center'
    });
    slide2.addText(stat.value, {
      x: x, y: 1.65, w: 2.2, h: 0.6,
      fontSize: 24, bold: true, color: stat.color, align: 'center'
    });
  });
  
  // Executive Summary Text
  let summaryText = '';
  const execSummary = data.ai_analysis?.executive_summary;
  
  if (typeof execSummary === 'string' && execSummary.length > 50) {
    summaryText = execSummary.substring(0, 1500);
  } else {
    const level = data.maturity_level?.level || data.overall_maturity_level || 'Developing';
    const gapStatus = overallScore < (benchmark.avg || 2.5) ? 'below' : 'above';
    const gap = Math.abs(overallScore - (benchmark.avg || 2.5)).toFixed(2);
    
    summaryText = `${data.company_name || results.company_name} has achieved a Data Maturity score of ${overallScore.toFixed(1)}/5.0, placing the organization at the ${level} level. This score is ${gap} points ${gapStatus} the ${data.industry || results.industry} industry average of ${benchmark.avg || 2.5}.

The assessment reveals opportunities for improvement across the 7 key dimensions of data management. Based on our analysis, focused investments in data governance, quality management, and analytics capabilities are recommended.

Implementation of the 12-month transformation roadmap can help advance the organization's data maturity while realizing significant business value through improved decision-making and operational efficiency.`;
  }
  
  slide2.addText(summaryText, {
    x: 0.5, y: 2.6, w: 9, h: 2.5,
    fontSize: 11, color: colors.darkGray, valign: 'top'
  });

  // ============== Slide 3: Data Maturity Ladder ==============
  const slide3 = pptx.addSlide();
  
  slide3.addShape('rect', {
    x: 0, y: 0, w: 10, h: 1,
    fill: { color: colors.secondary }
  });
  slide3.addText('Data Maturity Model', {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 28, bold: true, color: colors.white
  });
  
  const currentLevel = Math.min(Math.max(Math.round(overallScore), 1), 5) - 1;
  
  maturityLevels.forEach((lvl, idx) => {
    const y = 4.4 - idx * 0.7;
    const isCurrentLevel = idx === currentLevel;
    const isPastLevel = idx < currentLevel;
    
    // Level bar
    slide3.addShape('rect', {
      x: 0.5, y: y, w: 1.5 + (idx + 1) * 0.8, h: 0.55,
      fill: { color: isCurrentLevel ? colors.primary : isPastLevel ? colors.emerald : colors.lightGray }
    });
    
    // Level number
    slide3.addText(`L${lvl.level}`, {
      x: 0.55, y: y + 0.1, w: 0.4, h: 0.35,
      fontSize: 10, bold: true, color: isCurrentLevel || isPastLevel ? colors.white : colors.mediumGray
    });
    
    // Level name
    slide3.addText(lvl.name, {
      x: 5.5, y: y, w: 2.5, h: 0.3,
      fontSize: 10, bold: true, color: isCurrentLevel ? colors.primary : colors.darkGray
    });
    
    // Description
    slide3.addText(lvl.desc, {
      x: 5.5, y: y + 0.28, w: 4, h: 0.25,
      fontSize: 8, color: colors.mediumGray
    });
    
    // Current position indicator
    if (isCurrentLevel) {
      slide3.addText('← YOU ARE HERE', {
        x: 2.5 + (idx + 1) * 0.8, y: y + 0.1, w: 1.5, h: 0.35,
        fontSize: 9, bold: true, color: colors.primary
      });
    }
  });

  // ============== Slide 4: 7 Dimensions Overview ==============
  const slide4 = pptx.addSlide();
  
  slide4.addShape('rect', {
    x: 0, y: 0, w: 10, h: 1,
    fill: { color: colors.primary }
  });
  slide4.addText('7 Dimensions of Data Maturity', {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 28, bold: true, color: colors.white
  });
  
  // Dimension cards
  const dimColors = ['6366F1', '8B5CF6', '0EA5E9', '10B981', 'F59E0B', 'EF4444', '6366F1'];
  
  if (dimensions && dimensions.length > 0) {
    dimensions.forEach((dim, idx) => {
      const col = idx % 4;
      const row = Math.floor(idx / 4);
      const x = 0.4 + col * 2.4;
      const y = 1.2 + row * 2.1;
      
      const score = dim.score || 0;
      const dimName = dim.dimension_name || dim.name || `Dimension ${idx + 1}`;
      const shortName = dimName.length > 20 ? dimName.substring(0, 18) + '...' : dimName;
      
      // Card background
      slide4.addShape('rect', {
        x: x, y: y, w: 2.2, h: 1.9,
        fill: { color: colors.white },
        shadow: { type: 'outer', blur: 3, offset: 2, angle: 45, opacity: 0.3 }
      });
      
      // Color bar
      slide4.addShape('rect', {
        x: x, y: y, w: 2.2, h: 0.15,
        fill: { color: dimColors[idx] || colors.primary }
      });
      
      // Dimension name
      slide4.addText(shortName, {
        x: x + 0.1, y: y + 0.25, w: 2, h: 0.55,
        fontSize: 9, bold: true, color: colors.darkGray, wrap: true
      });
      
      // Score
      slide4.addText(score.toFixed(1), {
        x: x + 0.1, y: y + 0.85, w: 1.2, h: 0.5,
        fontSize: 22, bold: true, color: getScoreColor(score)
      });
      
      // out of 5.0
      slide4.addText('/5.0', {
        x: x + 1.1, y: y + 0.95, w: 0.8, h: 0.3,
        fontSize: 10, color: colors.mediumGray
      });
      
      // Status
      const status = score >= benchmark.avg ? 'On Track' : score >= benchmark.avg - 0.5 ? 'At Risk' : 'Critical';
      const statusColor = status === 'On Track' ? colors.green : status === 'At Risk' ? colors.amber : colors.red;
      slide4.addText(status, {
        x: x + 0.1, y: y + 1.45, w: 2, h: 0.3,
        fontSize: 9, bold: true, color: statusColor
      });
    });
  }

  // ============== Slide 5: Dimension Analysis (Page 1) ==============
  const slide5 = pptx.addSlide();
  
  slide5.addShape('rect', {
    x: 0, y: 0, w: 10, h: 1,
    fill: { color: colors.secondary }
  });
  slide5.addText('Detailed Dimension Analysis', {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 28, bold: true, color: colors.white
  });
  
  // Analysis from AI - try multiple possible locations
  const aiAnalysis = data.ai_analysis?.dimension_analysis || data.dimension_analysis || [];
  
  // Helper function to find matching analysis for a dimension
  const findDimensionAnalysis = (dimName, dimId) => {
    if (!Array.isArray(aiAnalysis) || aiAnalysis.length === 0) return null;
    
    // Try multiple matching strategies
    return aiAnalysis.find(a => {
      if (!a) return false;
      // Match by ID
      if (dimId && (a.dimension_id === dimId || a.id === dimId)) return true;
      // Match by exact name
      if (a.dimension === dimName || a.name === dimName) return true;
      // Match by partial name (first word)
      const searchTerm = dimName.toLowerCase().split(' ')[0];
      const analysisName = (a.dimension || a.name || '').toLowerCase();
      return analysisName.includes(searchTerm) || searchTerm.includes(analysisName.split(' ')[0]);
    });
  };
  
  // Get specific recommendation based on dimension and score
  const getSpecificRecommendation = (dimName, score, analysis) => {
    // If we have AI analysis, use it
    if (analysis) {
      const recs = analysis.quick_wins || analysis.recommendations || analysis.long_term_improvements || [];
      if (Array.isArray(recs) && recs.length > 0) {
        return typeof recs[0] === 'string' ? recs[0] : recs[0].recommendation || recs[0].action || String(recs[0]);
      }
      if (analysis.current_state) {
        return analysis.current_state.substring(0, 150);
      }
    }
    
    // Generate specific recommendations based on dimension name and score
    const dimLower = dimName.toLowerCase();
    if (score < 2) {
      if (dimLower.includes('governance')) return 'URGENT: Establish formal data governance council with executive sponsorship and define clear data ownership accountability';
      if (dimLower.includes('quality')) return 'CRITICAL: Implement automated data quality profiling and establish data quality SLAs for critical data domains';
      if (dimLower.includes('architecture')) return 'PRIORITY: Conduct data architecture assessment and develop target state architecture with modern data platform';
      if (dimLower.includes('integration')) return 'IMMEDIATE: Map critical data flows and implement real-time data integration for key business processes';
      if (dimLower.includes('security')) return 'URGENT: Deploy data classification framework and implement role-based access controls across all data assets';
      if (dimLower.includes('metadata')) return 'CRITICAL: Deploy enterprise data catalog and begin documenting critical data assets with business glossary';
      if (dimLower.includes('culture') || dimLower.includes('dataops')) return 'FOUNDATIONAL: Launch data literacy program and establish DataOps practices for continuous improvement';
    } else if (score < 3) {
      if (dimLower.includes('governance')) return 'Formalize data stewardship program and implement data governance metrics dashboard';
      if (dimLower.includes('quality')) return 'Expand data quality monitoring coverage and implement proactive data quality alerting';
      if (dimLower.includes('architecture')) return 'Modernize data architecture with cloud-native solutions and implement data mesh patterns';
      if (dimLower.includes('integration')) return 'Implement event-driven architecture for real-time data synchronization';
      if (dimLower.includes('security')) return 'Enhance data privacy controls and implement automated PII discovery and masking';
      if (dimLower.includes('metadata')) return 'Expand metadata coverage and implement automated lineage tracking';
      if (dimLower.includes('culture') || dimLower.includes('dataops')) return 'Scale data literacy across business units and implement self-service analytics';
    } else {
      if (dimLower.includes('governance')) return 'Optimize governance processes with AI-assisted policy enforcement and predictive compliance';
      if (dimLower.includes('quality')) return 'Implement ML-based anomaly detection and predictive data quality management';
      if (dimLower.includes('architecture')) return 'Advance to intelligent data fabric with automated optimization';
      if (dimLower.includes('integration')) return 'Implement zero-latency data mesh with intelligent routing';
      if (dimLower.includes('security')) return 'Deploy advanced threat detection with AI-powered security analytics';
      if (dimLower.includes('metadata')) return 'Implement knowledge graphs and semantic data discovery';
      if (dimLower.includes('culture') || dimLower.includes('dataops')) return 'Achieve data-driven culture with embedded analytics and citizen data scientists';
    }
    return `Strengthen ${dimName.toLowerCase()} capabilities with targeted improvements based on current maturity level`;
  };
  
  const firstFourDims = dimensions.slice(0, 4);
  firstFourDims.forEach((dim, idx) => {
    const y = 1.2 + idx * 1.2;
    const score = dim.score || 0;
    const dimName = dim.dimension_name || dim.name || `Dimension ${idx + 1}`;
    const dimId = dim.dimension_id || dim.id;
    
    // Find AI analysis for this dimension
    const analysis = findDimensionAnalysis(dimName, dimId);
    
    // Dimension header
    slide5.addText(`${dimName}: ${score.toFixed(1)}/5.0`, {
      x: 0.5, y: y, w: 4.5, h: 0.35,
      fontSize: 12, bold: true, color: colors.darkGray
    });
    
    // Score bar
    slide5.addShape('rect', {
      x: 5.5, y: y + 0.05, w: 4, h: 0.25,
      fill: { color: colors.lightGray }
    });
    slide5.addShape('rect', {
      x: 5.5, y: y + 0.05, w: (score / 5) * 4, h: 0.25,
      fill: { color: getScoreColor(score) }
    });
    
    // Key recommendation - now specific
    const keyRec = getSpecificRecommendation(dimName, score, analysis);
    
    slide5.addText(`→ ${keyRec}`, {
      x: 0.5, y: y + 0.4, w: 9, h: 0.6,
      fontSize: 9, color: colors.mediumGray, wrap: true
    });
  });

  // ============== Slide 6: Dimension Analysis (Page 2) ==============
  if (dimensions.length > 4) {
    const slide6 = pptx.addSlide();
    
    slide6.addShape('rect', {
      x: 0, y: 0, w: 10, h: 1,
      fill: { color: colors.secondary }
    });
    slide6.addText('Detailed Dimension Analysis (Continued)', {
      x: 0.5, y: 0.3, w: 9, h: 0.5,
      fontSize: 28, bold: true, color: colors.white
    });
    
    const remainingDims = dimensions.slice(4);
    remainingDims.forEach((dim, idx) => {
      const y = 1.2 + idx * 1.2;
      const score = dim.score || 0;
      const dimName = dim.dimension_name || dim.name || `Dimension ${idx + 5}`;
      const dimId = dim.dimension_id || dim.id;
      
      const analysis = findDimensionAnalysis(dimName, dimId);
      
      slide6.addText(`${dimName}: ${score.toFixed(1)}/5.0`, {
        x: 0.5, y: y, w: 4.5, h: 0.35,
        fontSize: 12, bold: true, color: colors.darkGray
      });
      
      slide6.addShape('rect', {
        x: 5.5, y: y + 0.05, w: 4, h: 0.25,
        fill: { color: colors.lightGray }
      });
      slide6.addShape('rect', {
        x: 5.5, y: y + 0.05, w: (score / 5) * 4, h: 0.25,
        fill: { color: getScoreColor(score) }
      });
      
      const keyRec = getSpecificRecommendation(dimName, score, analysis);
      
      slide6.addText(`→ ${keyRec}`, {
        x: 0.5, y: y + 0.4, w: 9, h: 0.6,
        fontSize: 9, color: colors.mediumGray, wrap: true
      });
    });
  }

  // ============== Slide 7: Industry Benchmarking ==============
  const slide7 = pptx.addSlide();
  
  slide7.addShape('rect', {
    x: 0, y: 0, w: 10, h: 1,
    fill: { color: colors.primary }
  });
  slide7.addText('Industry Benchmarking', {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 28, bold: true, color: colors.white
  });
  
  // Benchmark comparison bars
  const benchmarkData = [
    { label: 'Your Score', value: overallScore, color: getScoreColor(overallScore) },
    { label: 'Industry Average', value: benchmark.avg || 2.5, color: colors.mediumGray },
    { label: 'Top Performers', value: benchmark.top || 4.0, color: colors.emerald }
  ];
  
  benchmarkData.forEach((item, idx) => {
    const y = 1.5 + idx * 1.2;
    
    slide7.addText(item.label, {
      x: 0.5, y: y, w: 2, h: 0.4,
      fontSize: 12, color: colors.darkGray
    });
    
    // Bar background
    slide7.addShape('rect', {
      x: 2.8, y: y + 0.05, w: 5, h: 0.5,
      fill: { color: colors.lightGray }
    });
    
    // Bar fill
    slide7.addShape('rect', {
      x: 2.8, y: y + 0.05, w: (item.value / 5) * 5, h: 0.5,
      fill: { color: item.color }
    });
    
    // Score value
    slide7.addText(item.value.toFixed(1), {
      x: 8, y: y, w: 1, h: 0.4,
      fontSize: 16, bold: true, color: item.color
    });
  });
  
  // Gap analysis
  const gapToAvg = overallScore - (benchmark.avg || 2.5);
  const gapToTop = overallScore - (benchmark.top || 4.0);
  
  slide7.addShape('rect', {
    x: 0.5, y: 4.8, w: 4.3, h: 1.4,
    fill: { color: gapToAvg >= 0 ? 'DCFCE7' : 'FEE2E2' }
  });
  slide7.addText('Gap to Industry Average', {
    x: 0.7, y: 4.9, w: 3.9, h: 0.35,
    fontSize: 10, color: colors.darkGray
  });
  slide7.addText(`${gapToAvg >= 0 ? '+' : ''}${gapToAvg.toFixed(2)}`, {
    x: 0.7, y: 5.3, w: 3.9, h: 0.6,
    fontSize: 28, bold: true, color: gapToAvg >= 0 ? colors.green : colors.red
  });
  
  slide7.addShape('rect', {
    x: 5.2, y: 4.8, w: 4.3, h: 1.4,
    fill: { color: gapToTop >= 0 ? 'DCFCE7' : 'FEF3C7' }
  });
  slide7.addText('Gap to Top Performers', {
    x: 5.4, y: 4.9, w: 3.9, h: 0.35,
    fontSize: 10, color: colors.darkGray
  });
  slide7.addText(`${gapToTop >= 0 ? '+' : ''}${gapToTop.toFixed(2)}`, {
    x: 5.4, y: 5.3, w: 3.9, h: 0.6,
    fontSize: 28, bold: true, color: gapToTop >= 0 ? colors.green : colors.amber
  });

  // ============== Slide 8: Transformation Roadmap ==============
  const slide8 = pptx.addSlide();
  
  slide8.addShape('rect', {
    x: 0, y: 0, w: 10, h: 1,
    fill: { color: colors.secondary }
  });
  slide8.addText('12-Month Transformation Roadmap', {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 28, bold: true, color: colors.white
  });
  
  const roadmap = data.ai_analysis?.transformation_roadmap || data.ai_analysis?.strategic_roadmap || data.ai_analysis?.roadmap || data.roadmap || {};
  
  // Default initiatives based on score and common data maturity practices
  const getDefaultInitiatives = (phaseName, overallScore) => {
    const isLowMaturity = overallScore < 2.5;
    const isMidMaturity = overallScore >= 2.5 && overallScore < 3.5;
    
    const defaultPhases = {
      'Foundation': {
        focus: isLowMaturity 
          ? ['Data Governance Setup', 'Current State Assessment'] 
          : ['Governance Enhancement', 'Process Standardization'],
        initiatives: isLowMaturity 
          ? ['Form Data Governance Council with executive sponsor', 'Complete data landscape inventory & catalog', 'Define data ownership RACI matrix', 'Establish data quality baseline metrics']
          : ['Enhance governance policies with automation', 'Implement data stewardship workflows', 'Deploy metadata management platform', 'Standardize data definitions enterprise-wide']
      },
      'Build': {
        focus: isLowMaturity 
          ? ['Data Quality Framework', 'Architecture Foundation'] 
          : ['Quality Automation', 'Integration Excellence'],
        initiatives: isLowMaturity 
          ? ['Implement data quality profiling tools', 'Design target data architecture', 'Deploy master data management for key domains', 'Establish data security & privacy controls']
          : ['Deploy automated data quality monitoring', 'Implement real-time data integration', 'Scale MDM to additional domains', 'Enable self-service data access']
      },
      'Scale': {
        focus: isLowMaturity 
          ? ['Process Automation', 'Enterprise Rollout'] 
          : ['Advanced Analytics', 'DataOps Maturity'],
        initiatives: isLowMaturity 
          ? ['Automate data ingestion pipelines', 'Roll out governance to all business units', 'Implement data lineage tracking', 'Launch data literacy program organization-wide']
          : ['Deploy advanced analytics platform', 'Implement CI/CD for data pipelines', 'Enable predictive data quality', 'Build data product marketplace']
      },
      'Optimize': {
        focus: isLowMaturity 
          ? ['Continuous Improvement', 'AI Readiness'] 
          : ['AI/ML Enablement', 'Data Monetization'],
        initiatives: isLowMaturity 
          ? ['Implement continuous governance monitoring', 'Prepare data foundation for AI/ML', 'Establish data value metrics', 'Create center of excellence']
          : ['Deploy ML feature store', 'Implement intelligent data fabric', 'Launch data monetization initiatives', 'Achieve industry-leading data culture']
      }
    };
    return defaultPhases[phaseName] || { focus: ['Strategic Planning'], initiatives: ['Define strategic objectives'] };
  };
  
  const phases = [
    { key: 'phase_1', name: 'Foundation', months: 'Months 1-3', color: colors.primary },
    { key: 'phase_2', name: 'Build', months: 'Months 4-6', color: colors.secondary },
    { key: 'phase_3', name: 'Scale', months: 'Months 7-9', color: colors.accent },
    { key: 'phase_4', name: 'Optimize', months: 'Months 10-12', color: colors.emerald }
  ];
  
  phases.forEach((phase, idx) => {
    const x = 0.3 + idx * 2.4;
    const phaseData = roadmap[phase.key] || {};
    const defaults = getDefaultInitiatives(phase.name, overallScore);
    
    // Phase header
    slide8.addShape('rect', {
      x: x, y: 1.2, w: 2.3, h: 0.6,
      fill: { color: phase.color }
    });
    slide8.addText(phase.name, {
      x: x, y: 1.25, w: 2.3, h: 0.3,
      fontSize: 12, bold: true, color: colors.white, align: 'center'
    });
    slide8.addText(phase.months, {
      x: x, y: 1.5, w: 2.3, h: 0.25,
      fontSize: 8, color: colors.white, align: 'center'
    });
    
    // Phase content
    slide8.addShape('rect', {
      x: x, y: 1.85, w: 2.3, h: 3.5,
      fill: { color: colors.lightGray }
    });
    
    // Get initiatives from AI or defaults
    let initiatives = [];
    if (phaseData.key_initiatives && Array.isArray(phaseData.key_initiatives) && phaseData.key_initiatives.length > 0) {
      initiatives = phaseData.key_initiatives;
    } else if (phaseData.initiatives && Array.isArray(phaseData.initiatives) && phaseData.initiatives.length > 0) {
      initiatives = phaseData.initiatives;
    } else if (phaseData.key_activities && Array.isArray(phaseData.key_activities) && phaseData.key_activities.length > 0) {
      initiatives = phaseData.key_activities;
    } else {
      initiatives = defaults.initiatives;
    }
    
    let focusAreas = [];
    if (phaseData.focus_areas && Array.isArray(phaseData.focus_areas) && phaseData.focus_areas.length > 0) {
      focusAreas = phaseData.focus_areas;
    } else {
      focusAreas = defaults.focus;
    }
    
    let contentY = 1.95;
    
    // Focus areas
    slide8.addText('Focus:', {
      x: x + 0.1, y: contentY, w: 2.1, h: 0.25,
      fontSize: 8, bold: true, color: colors.darkGray
    });
    contentY += 0.25;
    
    focusAreas.slice(0, 2).forEach(area => {
      const areaText = typeof area === 'string' ? area : (area.area || area.name || String(area));
      slide8.addText(`• ${areaText}`, {
        x: x + 0.1, y: contentY, w: 2.1, h: 0.35,
        fontSize: 7, color: colors.mediumGray, wrap: true
      });
      contentY += 0.35;
    });
    
    contentY += 0.1;
    
    // Initiatives
    slide8.addText('Initiatives:', {
      x: x + 0.1, y: contentY, w: 2.1, h: 0.25,
      fontSize: 8, bold: true, color: colors.darkGray
    });
    contentY += 0.25;
    
    initiatives.slice(0, 4).forEach(initiative => {
      const initText = typeof initiative === 'string' ? initiative : (initiative.name || initiative.initiative || String(initiative));
      const truncatedText = initText.length > 45 ? initText.substring(0, 42) + '...' : initText;
      slide8.addText(`• ${truncatedText}`, {
        x: x + 0.1, y: contentY, w: 2.1, h: 0.4,
        fontSize: 6.5, color: colors.mediumGray, wrap: true
      });
      contentY += 0.38;
    });
  });

  // ============== Slide 9: Key Recommendations ==============
  const slide9 = pptx.addSlide();
  
  slide9.addShape('rect', {
    x: 0, y: 0, w: 10, h: 1,
    fill: { color: colors.primary }
  });
  slide9.addText('Key Recommendations', {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 28, bold: true, color: colors.white
  });
  
  // Get recommendations from AI analysis - with proper object handling
  const criticalGaps = data.ai_analysis?.critical_pain_points || data.ai_analysis?.areas_of_improvement?.critical_gaps || [];
  const recommendations = data.ai_analysis?.key_recommendations || data.ai_analysis?.recommendations || [];
  
  // Helper to safely extract text from various object structures
  const extractGapText = (gap) => {
    if (typeof gap === 'string') return gap;
    if (gap.area) return `${gap.area}: ${gap.current_impact || gap.risk_if_unaddressed || gap.recommended_action || ''}`;
    if (gap.description) return gap.description;
    if (gap.gap) return gap.gap;
    if (gap.title) return `${gap.title}: ${gap.description || ''}`;
    return JSON.stringify(gap).replace(/[{}"]/g, '').substring(0, 120);
  };
  
  // Generate default critical areas based on lowest scoring dimensions
  const getDefaultCriticalAreas = () => {
    if (!dimensions || dimensions.length === 0) return [];
    const sortedDims = [...dimensions].sort((a, b) => (a.score || 0) - (b.score || 0));
    return sortedDims.slice(0, 3).map(dim => {
      const name = dim.dimension_name || dim.name;
      const score = dim.score || 0;
      if (score < 1.5) return `CRITICAL: ${name} requires immediate attention - establish foundational capabilities`;
      if (score < 2.5) return `HIGH PRIORITY: ${name} needs focused improvement to meet industry standards`;
      return `MONITOR: ${name} should be strengthened to achieve competitive advantage`;
    });
  };
  
  let recY = 1.3;
  
  // Critical Areas
  const gaps = criticalGaps.length > 0 ? criticalGaps : getDefaultCriticalAreas();
  
  if (gaps.length > 0) {
    slide9.addText('Critical Areas Requiring Attention:', {
      x: 0.5, y: recY, w: 9, h: 0.4,
      fontSize: 14, bold: true, color: colors.red
    });
    recY += 0.5;
    
    gaps.slice(0, 3).forEach((gap, idx) => {
      const gapText = extractGapText(gap);
      slide9.addShape('rect', {
        x: 0.5, y: recY, w: 0.15, h: 0.5,
        fill: { color: colors.red }
      });
      slide9.addText(gapText.substring(0, 120), {
        x: 0.8, y: recY, w: 8.5, h: 0.55,
        fontSize: 10, color: colors.darkGray, wrap: true
      });
      recY += 0.65;
    });
    recY += 0.3;
  }
  
  // Generate score-specific recommendations
  const getScoreBasedRecommendations = () => {
    if (overallScore < 2) {
      return [
        'IMMEDIATE: Establish data governance council with C-level sponsorship and define clear data ownership',
        'CRITICAL: Deploy data catalog and begin inventorying critical data assets across the organization',
        'HIGH: Implement basic data quality monitoring for key business data domains',
        'PRIORITY: Launch data literacy awareness program for business stakeholders'
      ];
    } else if (overallScore < 3) {
      return [
        'Formalize data stewardship roles and implement governance workflows with metrics tracking',
        'Deploy automated data quality profiling and establish SLAs for critical data domains',
        'Implement master data management for customer and product data with golden records',
        'Scale data literacy program and enable self-service analytics for business users'
      ];
    } else {
      return [
        'Advance to AI-powered data governance with automated policy enforcement',
        'Implement predictive data quality with ML-based anomaly detection',
        'Deploy intelligent data fabric with automated optimization and discovery',
        'Establish data mesh architecture with domain-oriented ownership'
      ];
    }
  };
  
  // Top Recommendations
  slide9.addText('Strategic Recommendations:', {
    x: 0.5, y: recY, w: 9, h: 0.4,
    fontSize: 14, bold: true, color: colors.primary
  });
  recY += 0.5;
  
  let recs = [];
  if (recommendations.length > 0) {
    recs = recommendations.map(rec => {
      if (typeof rec === 'string') return rec;
      return rec.recommendation || rec.action || rec.description || String(rec);
    });
  } else {
    recs = getScoreBasedRecommendations();
  }
  
  recs.slice(0, 4).forEach((rec, idx) => {
    const recText = typeof rec === 'string' ? rec : String(rec);
    slide9.addText(`${idx + 1}. ${recText.substring(0, 110)}`, {
      x: 0.5, y: recY, w: 9, h: 0.55,
      fontSize: 10, color: colors.darkGray, wrap: true
    });
    recY += 0.6;
  });

  // ============== Slide 10: Level Progression - Journey to Next Level ==============
  const levelProgression = data.ai_analysis?.level_progression;
  if (levelProgression) {
    const slideLP = pptx.addSlide();
    
    // Header
    slideLP.addShape('rect', {
      x: 0, y: 0, w: 10, h: 1.1,
      fill: { color: colors.emerald }
    });
    slideLP.addText('Level Up: Journey to Next Maturity Level', {
      x: 0.5, y: 0.2, w: 9, h: 0.4,
      fontSize: 24, bold: true, color: colors.white
    });
    slideLP.addText('Time, Effort, Investment & Benefits Analysis', {
      x: 0.5, y: 0.6, w: 9, h: 0.3,
      fontSize: 11, color: 'FFFFFF'
    });

    // Current to Next Level Visual
    const currentLevel = levelProgression.current_level?.level_number || Math.floor(overallScore);
    const nextLevel = levelProgression.next_level?.level_number || Math.min(currentLevel + 1, 5);
    const currentLevelName = levelProgression.current_level?.level_name || maturityLevel;
    const nextLevelName = levelProgression.next_level?.level_name || 'Next Level';
    
    // Current Level Circle
    slideLP.addShape('ellipse', {
      x: 1.5, y: 1.3, w: 1.2, h: 1.2,
      fill: { color: colors.orange }
    });
    slideLP.addText(String(currentLevel), {
      x: 1.5, y: 1.55, w: 1.2, h: 0.7,
      fontSize: 28, bold: true, color: colors.white, align: 'center'
    });
    slideLP.addText('Current', {
      x: 1.2, y: 2.5, w: 1.8, h: 0.3,
      fontSize: 10, bold: true, color: colors.darkGray, align: 'center'
    });
    slideLP.addText(currentLevelName, {
      x: 1, y: 2.75, w: 2.2, h: 0.3,
      fontSize: 9, color: colors.mediumGray, align: 'center'
    });

    // Arrow with time
    slideLP.addShape('rect', {
      x: 3.2, y: 1.75, w: 3.6, h: 0.3,
      fill: { color: colors.emerald }
    });
    slideLP.addText(levelProgression.progression_requirements?.time_estimate || '6-9 months', {
      x: 3.2, y: 2.1, w: 3.6, h: 0.3,
      fontSize: 10, bold: true, color: colors.emerald, align: 'center'
    });

    // Next Level Circle
    slideLP.addShape('ellipse', {
      x: 7.3, y: 1.3, w: 1.2, h: 1.2,
      fill: { color: colors.emerald }
    });
    slideLP.addText(String(nextLevel), {
      x: 7.3, y: 1.55, w: 1.2, h: 0.7,
      fontSize: 28, bold: true, color: colors.white, align: 'center'
    });
    slideLP.addText('Target', {
      x: 7, y: 2.5, w: 1.8, h: 0.3,
      fontSize: 10, bold: true, color: colors.darkGray, align: 'center'
    });
    slideLP.addText(nextLevelName, {
      x: 6.8, y: 2.75, w: 2.2, h: 0.3,
      fontSize: 9, color: colors.emerald, align: 'center'
    });

    // Resource Requirements Grid
    const reqY = 3.2;
    const reqCards = [
      { label: 'TIME', value: levelProgression.progression_requirements?.time_estimate || '6-9 months', color: '3B82F6' },
      { label: 'MANPOWER', value: levelProgression.progression_requirements?.manpower_resources?.internal_fte || 'Not available', color: '8B5CF6' },
      { label: 'INVESTMENT', value: levelProgression.progression_requirements?.investment_required?.total_range || 'Not available', color: colors.emerald },
      { label: 'ROI', value: levelProgression.progression_requirements?.business_benefits?.quantified_value || 'Not available', color: colors.amber }
    ];

    reqCards.forEach((card, idx) => {
      const x = 0.5 + idx * 2.4;
      
      // Card background
      slideLP.addShape('rect', {
        x: x, y: reqY, w: 2.2, h: 1.2,
        fill: { color: colors.lightGray }
      });
      
      // Card header
      slideLP.addShape('rect', {
        x: x, y: reqY, w: 2.2, h: 0.35,
        fill: { color: card.color }
      });
      slideLP.addText(card.label, {
        x: x, y: reqY + 0.05, w: 2.2, h: 0.25,
        fontSize: 9, bold: true, color: colors.white, align: 'center'
      });
      
      // Card value
      slideLP.addText(card.value, {
        x: x + 0.1, y: reqY + 0.45, w: 2, h: 0.65,
        fontSize: 11, bold: true, color: card.color, align: 'center', valign: 'middle'
      });
    });

    // Business Benefits Section
    const benY = 4.6;
    slideLP.addText('Business Benefits at Next Level', {
      x: 0.5, y: benY, w: 9, h: 0.35,
      fontSize: 12, bold: true, color: colors.primary
    });

    const benefits = [
      { label: 'Efficiency Gains', value: levelProgression.progression_requirements?.business_benefits?.efficiency_gains || 'Not available' },
      { label: 'Revenue Impact', value: levelProgression.progression_requirements?.business_benefits?.revenue_impact || 'Not available' },
      { label: 'Risk Reduction', value: levelProgression.progression_requirements?.business_benefits?.risk_reduction || 'Not available' }
    ];

    benefits.forEach((ben, idx) => {
      const x = 0.5 + idx * 3.1;
      slideLP.addShape('rect', {
        x: x, y: benY + 0.4, w: 2.9, h: 0.7,
        fill: { color: 'ECFDF5' },
        line: { color: colors.emerald, width: 1 }
      });
      slideLP.addText(ben.label, {
        x: x + 0.1, y: benY + 0.45, w: 2.7, h: 0.25,
        fontSize: 8, color: colors.mediumGray
      });
      slideLP.addText(ben.value, {
        x: x + 0.1, y: benY + 0.7, w: 2.7, h: 0.35,
        fontSize: 10, bold: true, color: colors.emerald
      });
    });
  }

  // ============== Slide 11 (or 10 if no level progression): Next Steps ==============
  const slide10 = pptx.addSlide();
  slide10.background = { color: colors.dark };
  
  slide10.addText('Next Steps', {
    x: 0.5, y: 1, w: 9, h: 0.7,
    fontSize: 36, bold: true, color: colors.white
  });
  
  const nextSteps = [
    'Review detailed findings with key stakeholders',
    'Prioritize quick wins for immediate impact',
    'Develop detailed implementation plan',
    'Establish governance and change management',
    'Begin Phase 1 transformation initiatives'
  ];
  
  nextSteps.forEach((step, idx) => {
    slide10.addText(`${idx + 1}. ${step}`, {
      x: 0.5, y: 2 + idx * 0.6, w: 9, h: 0.5,
      fontSize: 14, color: colors.white
    });
  });
  
  // Contact
  slide10.addText('For questions, contact: karna.vijay@gmail.com', {
    x: 0.5, y: 5, w: 9, h: 0.3,
    fontSize: 10, color: colors.accent
  });
  
  // SAMANVAI footer
  slide10.addText('SAMANVAI', {
    x: 7.5, y: 5, w: 2, h: 0.4,
    fontSize: 14, bold: true, color: colors.primary
  });
  slide10.addText('Agentic AI for CXOs', {
    x: 7.5, y: 5.3, w: 2, h: 0.3,
    fontSize: 9, color: colors.accent
  });

  // Save the presentation
  appendQualitySlide(pptx, fullResults || results);
  return pptx.writeFile({ fileName: `Data_Maturity_Assessment_${(data.company_name || 'Company').replace(/[^a-zA-Z0-9]/g, '_')}.pptx` });
};

export default generateDataMaturityPptx;
